/**
 * Yarn colorway identity matching.
 *
 * A yarn library row (`Yarn`) is one colorway: brand + yarn name + color
 * name + color number. A project references a colorway via the embedded
 * `ProjectYarn` object which carries the same fields by value. These
 * two sides should match when they represent the same colorway — but
 * users routinely enter incomplete data on one side or the other, so
 * the matcher has to be forgiving.
 *
 * The rule (build 1457, after Veronika's Cashmere classic / Fiordaliso
 * #637 consume-failure regression):
 *
 *   1. Yarn name must match (case-insensitive, trimmed).
 *   2. If BOTH sides have a non-empty color number, that number is the
 *      precise identity — color name is allowed to differ. Real-world
 *      colorways are typically labelled by a manufacturer number; the
 *      human-readable "Fiordaliso" may be missing on one side without
 *      changing which colorway is being referred to. (This is what bit
 *      Veronika: project ref had colorName='Fiordaliso' but the library
 *      row had colorName='' alongside colorNumber='637'; the old rule
 *      required colorName equality and refused to match.)
 *   3. Otherwise — at least one side lacks a color number — fall back
 *      to color-name matching with empty-side wildcard, same as before.
 */

export interface YarnColorwayRef {
  yarnName?: string;
  colorName?: string;
  colorNumber?: string;
  /** Stash row uuid captured when picked from inventory (build 1644). */
  stashUuid?: string;
}

interface YarnColorwayLibRef {
  uuid?: string;
  name?: string;
  colorName?: string;
  colorNumber?: string;
}

function norm(value: string | undefined): string {
  return (value || '').trim().toLowerCase();
}

/** True if `projectYarn` is the same colorway as `libYarn`. See module
 *  docblock for the matching rule. */
export function yarnMatchesLib(projectYarn: YarnColorwayRef, libYarn: YarnColorwayLibRef): boolean {
  if (norm(projectYarn.yarnName) !== norm(libYarn.name)) return false;
  const pNum = (projectYarn.colorNumber || '').trim();
  const lNum = (libYarn.colorNumber || '').trim();
  // Both sides have a color number — that's the precise identity.
  // Color name is allowed to differ (one side may have left it blank,
  // or used a different language for the human label).
  if (pNum !== '' && lNum !== '') {
    return pNum === lNum;
  }
  // Fall back to color-name match. Empty colorName on either side is a
  // wildcard (mirrors the colorNumber wildcard for the common "didn't
  // type the field on the project side" case).
  const pName = norm(projectYarn.colorName);
  const lName = norm(libYarn.colorName);
  if (pName === '' || lName === '') return true;
  return pName === lName;
}

/** True if two project yarn references point at the same colorway.
 *  Same matching rule as `yarnMatchesLib` (build 1457). */
export function yarnRefEquals(a: YarnColorwayRef, b: YarnColorwayRef): boolean {
  if (norm(a.yarnName) !== norm(b.yarnName)) return false;
  const an = (a.colorNumber || '').trim();
  const bn = (b.colorNumber || '').trim();
  if (an !== '' && bn !== '') {
    return an === bn;
  }
  const aName = norm(a.colorName);
  const bName = norm(b.colorName);
  if (aName === '' || bName === '') return true;
  return aName === bName;
}

/** How specifically `projectYarn` identifies `libYarn`. Only meaningful
 *  when `yarnMatchesLib` is already true.
 *
 *    4 — the same non-empty colour number (the precise identity)
 *    2 — the same non-empty colour name
 *    1 — both sides carry no colour data at all (blank refers to blank)
 *    0 — matched only through the empty-side wildcard
 */
function matchSpecificity(projectYarn: YarnColorwayRef, libYarn: YarnColorwayLibRef): number {
  const pNum = (projectYarn.colorNumber || '').trim();
  const lNum = (libYarn.colorNumber || '').trim();
  const pName = norm(projectYarn.colorName);
  const lName = norm(libYarn.colorName);
  let score = 0;
  if (pNum !== '' && pNum === lNum) score += 4;
  if (pName !== '' && pName === lName) score += 2;
  if (pNum === '' && lNum === '' && pName === '' && lName === '') score += 1;
  return score;
}

/** Resolve a project yarn reference to THE library colourway it refers to.
 *
 *  `yarnMatchesLib` is deliberately forgiving: an empty colour name on
 *  either side is a wildcard. That is right for answering "could these be
 *  the same colourway?", but wrong for picking one row out of a list — a
 *  stash row with no colour name and no colour number wildcard-matches
 *  every sibling reference, so `libs.find(yarnMatchesLib)` returned it for
 *  "Arwetta / Hnědá #827" just because it sorted first (Veronika,
 *  2026-09-20: brown showed "0 / 1 skein" once the blank olive row was
 *  allocated in the same form).
 *
 *  This picks the MOST SPECIFIC match instead; ties keep list order, so a
 *  lone wildcard match still resolves exactly as before.
 *
 *  Build 1644: a reference that carries `stashUuid` (picked "from
 *  inventory") resolves to that very row first — the only way to tell
 *  apart two colourways of one yarn that are BOTH blank. Guard: the uuid
 *  wins only while its row still matches the typed text, so a line the
 *  user re-typed to a different colour follows the text, never a stale
 *  pointer. A dangling uuid (row deleted, shared project) falls through
 *  to text matching. Always use this
 *  (never `find(yarnMatchesLib)`) when choosing a library row, and use
 *  `yarnRefResolvesTo` when asking whether a reference counts against a
 *  particular row. */
export function findLibYarn<L extends YarnColorwayLibRef>(
  projectYarn: YarnColorwayRef,
  libYarns: readonly L[],
): L | undefined {
  const uuid = (projectYarn.stashUuid || '').trim();
  if (uuid !== '') {
    const linked = libYarns.find(l => l.uuid === uuid);
    if (linked && yarnMatchesLib(projectYarn, linked)) return linked;
  }
  let best: L | undefined;
  let bestScore = -1;
  for (const lib of libYarns) {
    if (!yarnMatchesLib(projectYarn, lib)) continue;
    const score = matchSpecificity(projectYarn, lib);
    if (score > bestScore) {
      best = lib;
      bestScore = score;
    }
  }
  return best;
}

function sameLibRow<L extends { id?: number }>(a: L, b: L): boolean {
  if (a === b) return true;
  return a.id != null && a.id === b.id;
}

/** True if `projectYarn` resolves to exactly `libYarn` among `allLibYarns`
 *  (see `findLibYarn`). This is the question stock maths must ask: each
 *  reference claims skeins from ONE colourway, not from every colourway it
 *  loosely matches. */
export function yarnRefResolvesTo<L extends YarnColorwayLibRef & { id?: number }>(
  projectYarn: YarnColorwayRef,
  libYarn: L,
  allLibYarns: readonly L[],
): boolean {
  const best = findLibYarn(projectYarn, allLibYarns);
  return best != null && sameLibRow(best, libYarn);
}

/** Library lookup variant of yarnMatchesLib that adds a sibling
 *  fallback for project refs that left colorName blank. Symmetric with
 *  the rendering fallback used by `view/page.tsx::resolveLibYarn`: a
 *  ref with no colorName resolves to the first library colorway that
 *  shares its yarn name. Inventory math (in-progress totals, available
 *  counts, "Used in" filters) needs the same fallback so a blank-color
 *  project ref counts against the colorway it's actually drawn against
 *  in the UI. Refs with a real colorName fall through to strict
 *  matching — they're never silently rerouted to a different colorway.
 *
 *  Pass the *full* library list (`allLibYarns`); soft-deleted rows
 *  must already be filtered out by the caller. The library reference
 *  receives an `id`-bearing shape so the fallback only confirms a
 *  match when the resolution target is the very row the caller is
 *  asking about. */
export function yarnMatchesLibWithFallback<L extends { id?: number; name?: string; colorName?: string; colorNumber?: string }>(
  projectYarn: YarnColorwayRef,
  libYarn: L,
  allLibYarns: L[],
): boolean {
  // Best-match first: a reference counts against the ONE colourway it
  // resolves to, not every row it wildcard-matches (see findLibYarn).
  const best = findLibYarn(projectYarn, allLibYarns);
  if (best) return sameLibRow(best, libYarn);
  // `libYarn` may not be in `allLibYarns` (caller filtered differently).
  if (yarnMatchesLib(projectYarn, libYarn)) return true;
  const refColor = (projectYarn.colorName || '').trim();
  if (refColor !== '') return false;
  const refName = norm(projectYarn.yarnName);
  const libName = norm(libYarn.name);
  if (!refName || refName !== libName) return false;
  if (libYarn.id == null) return false;
  const sibling = allLibYarns.find(y => norm(y.name) === libName);
  return sibling?.id != null && sibling.id === libYarn.id;
}
